import { ChevronDownIcon, ChevronUpIcon } from '@/components/icons';
import { useAccessibilityTTS, useTickSoundInteraction } from '@/hooks';
import { stripAngleBlocks } from '@/utils/faq';
import type { DropdownCategorySection } from '@/utils/custom-menu/dropdown';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface DropdownFilterProps {
    data: DropdownCategorySection[];
    onQuestionSelect: (question: string, answer: string) => void;
}

export default function DropdownFilter({ data, onQuestionSelect }: DropdownFilterProps) {
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [activeButton, setActiveButton] = useState<string | null>(null);
    const firstQuestionRefs = useRef<{ [sectionId: string]: HTMLButtonElement | null }>({});
    const { t } = useTranslation();

    const { handleInteraction } = useTickSoundInteraction();
    // Custom TTS handling
    const { speak } = useAccessibilityTTS();

    const speakQuestionClick = (answer: string) => {
        speak(`${answer}`);
    };

    const speakQuestionFocus = (text: string, buttonKey: string) => {
        const isActive = isButtonActive(buttonKey);
        speak(`${isActive ? t('common.button.tts.status_selected') : t('common.button.tts.status_not_selected')} ${text} ${t('common.button.tts.button')}`);
    };

    const speakDropdownClick = (title: string, sectionId: string) => {
        const wasExpanded = isSectionExpanded(sectionId);
        toggleSection(sectionId);

        handleInteraction('', () => { });

        speak(`${title} ${t('common.button.tts.button')}`);

        // Focus on the first question item when expanding the dropdown
        if (!wasExpanded) {
            setTimeout(() => {
                const firstQuestionButton = firstQuestionRefs.current[sectionId];
                if (firstQuestionButton) {
                    firstQuestionButton.focus();
                }
            }, 100);
        }
    };

    const speakDropdownFocus = (text: string) => {
        speak(`${text} ${t('common.button.tts.button')}`);
    };

    const toggleSection = useCallback((sectionId: string) => {
        setExpandedSections((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(sectionId)) {
                newSet.delete(sectionId);
            } else {
                newSet.add(sectionId);
            }
            return newSet;
        });
    }, []);

    const handleButtonClick = useCallback(
        (buttonKey: string, question: string, answer: string) => {
            const formattedAnswer = stripAngleBlocks(answer);
            setActiveButton(buttonKey);
            onQuestionSelect(question, answer);
            speakQuestionClick(formattedAnswer);
        },
        [onQuestionSelect, speak]
    );

    const getButtonKey = (sectionId: string, itemId: string) => `${sectionId}-${itemId}`;

    const isSectionExpanded = (sectionId: string) => expandedSections.has(sectionId);
    const isButtonActive = (buttonKey: string) => activeButton === buttonKey;

    return (
        <div className={`${styles['dropdown-container']} scrollbar-custom-gray-99938f scrollbar-w-4`}>
            <div className={styles['dropdown-wrapper']}>
                {data.map((section) => (
                    <div
                        key={section.id}
                        className={`${styles['section-item']} ${isSectionExpanded(section.id) ? styles['section-item-expanded'] : styles['section-item-default']}`}
                        data-region-nav
                    >
                        <button
                            onFocus={() => speakDropdownFocus(section.title)}
                            type="button"
                            onClick={() => speakDropdownClick(section.title, section.id)}
                            className={styles['section-header'] + ' focus-border' + (isSectionExpanded(section.id) ? ' pb-[calc(28*var(--vh-unit))]' : '')}
                        >
                            <div
                                className={`${styles['section-title']} ${isSectionExpanded(section.id) ? styles['section-title-expanded'] : styles['section-title-default']
                                }`}
                            >
                                {section.title}
                            </div>
                            <div className="size-[calc(24*var(--vh-unit))]">
                                {isSectionExpanded(section.id) ? (
                                    <ChevronUpIcon className={styles['icon-expanded']} />
                                ) : (
                                    <ChevronDownIcon className={styles['icon-default']} />
                                )}
                            </div>
                        </button>

                        {isSectionExpanded(section.id) && (
                            <div
                                className={`${styles['section-container']} ${isSectionExpanded(section.id) ? styles['section-container-expanded'] : styles['section-container-default']
                                }`}
                            >
                                {section.items.map((item, itemIndex) => {
                                    const buttonKey = getButtonKey(section.id, item.id);
                                    const isFirstQuestion = itemIndex === 0;

                                    return (
                                        <button
                                            ref={(el) => {
                                                if (isFirstQuestion) {
                                                    firstQuestionRefs.current[section.id] = el;
                                                }
                                            }}
                                            onFocus={() => speakQuestionFocus(item.question, buttonKey)}
                                            key={buttonKey}
                                            onClick={() => handleButtonClick(buttonKey, item.question, item.answer)}
                                            className={`${styles['question-button-base']} ${
                                                isButtonActive(buttonKey) ? styles['question-button-active'] : styles['question-button-default']
                                            }`}
                                        >
                                            {item.question}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};
